import jsPDF from "jspdf";
import { fmtRp, fmtNum } from "@/lib/format";

export function exportSimulationPdf(input, result) {
  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  let y = 50;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(30, 41, 184);
  doc.text("Proper Energy — Ringkasan Simulasi PLTS", 40, y);
  y += 10;
  doc.setDrawColor(30, 41, 184);
  doc.line(40, y, pageW - 40, y);
  y += 24;

  doc.setFontSize(10);
  doc.setTextColor(60, 60, 60);
  doc.setFont("helvetica", "normal");
  doc.text(`Dihasilkan: ${new Date().toLocaleString("id-ID")}`, 40, y);
  doc.text(`Versi asumsi: ${result?.assumptions_version || "-"}`, 300, y);
  y += 20;

  const section = (title) => {
    y += 10;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(12);
    doc.setTextColor(15, 18, 38);
    doc.text(title, 40, y);
    y += 6;
    doc.setDrawColor(228, 232, 242);
    doc.line(40, y, pageW - 40, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(40, 40, 40);
  };

  const row = (label, value) => {
    doc.text(label, 40, y);
    doc.text(String(value ?? "-"), 260, y);
    y += 14;
    if (y > 760) { doc.addPage(); y = 50; }
  };

  // A. Data Client
  section("A. Data Client");
  row("Kota", input.kota || "-");
  row("Daya PLN", `${input.daya_pln_lain || input.daya_pln_va || "-"} VA`);
  row("Fase", input.fase);
  row("Konsumsi kWh/bulan", input.kwh_bulanan || "-");
  row("Tagihan Rp/bulan", input.tagihan_rp ? fmtRp(input.tagihan_rp) : "-");

  // B. Perhitungan Konsumsi
  section("B. Perhitungan Konsumsi");
  const anyOpt = result?.options?.Safer || result?.options?.Starter || result?.options?.Signature;
  row("Konsumsi harian (kWh)", anyOpt?.daily_kwh_consumption);
  row("Sumber estimasi", anyOpt?.consumption_source);
  row("PSH yang dipakai", result?.psh_used);
  row("Tarif referensi", fmtRp(result?.tarif_used) + "/kWh");

  // C. Kebutuhan Sistem (3 options)
  section("C. Perbandingan 3 Opsi PLTS Tumbuh");
  ["Starter", "Safer", "Signature"].forEach((tier) => {
    const o = result?.options?.[tier];
    if (!o) return;
    doc.setFont("helvetica", "bold");
    doc.text(`• ${tier}${result?.paling_sesuai_tier === tier ? "  — Paling sesuai" : ""}`, 40, y);
    y += 14;
    doc.setFont("helvetica", "normal");
    row("  Panel × kWp", `${o.package.panels} × ${o.package.panel_wp} Wp = ${fmtNum(o.package.kwp, 2)} kWp`);
    row("  Inverter", `${o.package.inverter_kw} kW`);
    row("  Baterai", `${o.package.battery_units} unit × ${o.package.battery_kwh_per_unit} kWh = ${fmtNum(o.package.battery_nominal_kwh, 2)} kWh nominal / ${fmtNum(o.package.battery_usable_kwh, 2)} kWh usable`);
    row("  Produksi PV harian", `${fmtNum(o.pv_daily_kwh, 2)} kWh`);
    row("  Energi offset harian", `${fmtNum(o.energy_offset_daily_kwh, 2)} kWh (${o.coverage_pct}% konsumsi)`);
    row("  Perkiraan penghematan/bulan", fmtRp(o.savings_rp_month));
    row("  Backup est. (beban prioritas)", `${o.backup_hours_est} jam`);
    row("  Harga referensi", fmtRp(o.price_rp));
    if (o.issues?.length) {
      doc.setTextColor(220, 38, 38);
      o.issues.forEach((it) => { row("  ! Catatan", it); });
      doc.setTextColor(40, 40, 40);
    }
    y += 4;
  });

  // D. Summary
  section("D. Summary");
  row("Paling sesuai", result?.paling_sesuai_tier || "Perlu desain khusus");
  if (result?.reasoning) row("Alasan", result.reasoning);

  // Asumsi
  section("Asumsi perhitungan");
  const a = result?.assumptions || {};
  row("Panel Wp", a.panel_wp);
  row("Baterai per modul", `${a.battery_kwh_per_unit} kWh`);
  row("Faktor rugi agregat", `${(a.pv_loss_factor * 100).toFixed(0)}%`);
  row("DoD", `${(a.dod * 100).toFixed(0)}%`);
  row("Reserve SOC minimum", `${(a.soc_min_default * 100).toFixed(0)}%`);
  row("Efisiensi charge/discharge", `${(a.eff_charge * 100).toFixed(0)}% / ${(a.eff_discharge * 100).toFixed(0)}%`);
  row("PSH normal / konservatif", `${a.psh_normal} / ${a.psh_conservative}`);

  // Disclaimer
  y += 10;
  doc.setFontSize(8);
  doc.setTextColor(90, 96, 121);
  const disc = "Dokumen ini adalah estimasi awal berbasis asumsi standar Proper Energy, bukan penawaran final. Angka produksi, penghematan, dan durasi backup bersifat ilustratif dan masih harus diverifikasi melalui survei, dokumen produk resmi, serta desain engineering. Harga tidak mencakup pajak dan biaya tambahan bila belum dinyatakan. Status data demo (bila ada) tidak boleh dipakai sebagai dasar pembelian.";
  const split = doc.splitTextToSize(disc, pageW - 80);
  doc.text(split, 40, y);

  doc.save(`proper-energy-simulasi-${Date.now()}.pdf`);
}
